// AttendeeChips.tsx — kollabierte Teilnehmer-Chip-Liste (Termine-SP5, A8):
// erste 4 Chips + „+N weitere" (Toggle expandiert; „weniger anzeigen" klappt
// zurück). Geteilt zwischen TerminFormWindow (editierbar, mit Entfernen-X) und
// TerminPeekPopover (nur lesend) — keine Doppelpflege (Plan 3.6.4).
//
// Bewusst mit Inline-Styles im Refined-Look (aub-Palette wie die bisherigen
// Chips im Termin-Fenster), damit die Komponente in beiden Portalen
// (WindowManager-Fenster UND body-Portal des Peeks) identisch aussieht.

import * as React from "react";
import { useState } from "react";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { REFINED } from "../risikoanalyseStep/tokens";
import { kollabierteTeilnehmer } from "../windowManager/terminFormLogik";

/** Ein Teilnehmer-Chip (Anzeige-Name + E-Mail als Tooltip/Key). */
export interface AttendeeChip {
    address: string;
    name: string;
}

const wrapStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    minWidth: 0,
};

const chipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor: REFINED.aub50,
    border: `1px solid ${REFINED.aub200}`,
    color: REFINED.aub,
    fontSize: "0.85rem",
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    maxWidth: 240,
    minWidth: 0,
};

const nameStyle: React.CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const removeBtnStyle: React.CSSProperties = {
    appearance: "none",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: REFINED.aub,
    display: "inline-flex",
    flexShrink: 0,
};

const toggleBtnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    border: `1px dashed ${REFINED.aub200}`,
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontSize: "0.85rem",
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    cursor: "pointer",
};

export const AttendeeChips: React.FC<{
    attendees: AttendeeChip[];
    /** Gesetzt ⇒ editierbar: jeder Chip bekommt ein Entfernen-X. */
    onRemove?: (address: string) => void;
    /** Sichtbare Chips im kollabierten Zustand (A8-Default: 4). */
    maxSichtbar?: number;
    style?: React.CSSProperties;
    /** Die E-Mail-Adresse unter dem Namen zeigen statt nur im Tooltip
     *  (Termin-Fenster, PO 28.09.2026). */
    mitAdresse?: boolean;
}> = ({ attendees, onRemove, maxSichtbar = 4, style, mitAdresse = false }) => {
    const [expanded, setExpanded] = useState(false);
    if (attendees.length === 0) return null;
    const { sichtbar, versteckt } = kollabierteTeilnehmer(attendees, expanded, maxSichtbar);
    return (
        <div style={{ ...wrapStyle, ...style }}>
            {sichtbar.map((a) => (
                <span
                    key={a.address}
                    style={mitAdresse ? { ...chipStyle, borderRadius: 12, maxWidth: 300 } : chipStyle}
                    title={a.address}
                >
                    {mitAdresse && a.name && a.name !== a.address ? (
                        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <span style={nameStyle}>{a.name}</span>
                            <span style={{ ...nameStyle, fontWeight: 400, fontSize: "0.78rem" }}>
                                {a.address}
                            </span>
                        </span>
                    ) : (
                        <span style={nameStyle}>{a.name}</span>
                    )}
                    {onRemove && (
                        <button
                            type="button"
                            style={removeBtnStyle}
                            aria-label="Teilnehmer entfernen"
                            onClick={() => onRemove(a.address)}
                        >
                            <Dismiss16Regular />
                        </button>
                    )}
                </span>
            ))}
            {versteckt > 0 && (
                <button type="button" style={toggleBtnStyle} onClick={() => setExpanded(true)}>
                    +{versteckt} weitere
                </button>
            )}
            {expanded && attendees.length > maxSichtbar && (
                <button type="button" style={toggleBtnStyle} onClick={() => setExpanded(false)}>
                    weniger anzeigen
                </button>
            )}
        </div>
    );
};

export default AttendeeChips;
